import type { ScmAuthApi } from '@backstage/integration-react';
import { GITHUB_ORG } from '../githubOrg';
import { githubGraphql } from './githubGraphql';
import type { App, DeployWorkflowsType } from '@internal/plugin-deployments';
import { DAY_MS, HOUR_MS, ageMs, timeAgo } from './time';
import { NeedsYouItem } from './types';

/** How far back a merged pull request is followed through its deploy. */
export const MERGED_DAYS = 3;
/** A deploy step running longer than this counts as stuck. */
export const STUCK_HOURS = 2;

const QUERY = `query MyMergedPullRequests($q: String!) {
  search(query: $q, type: ISSUE, first: 30) {
    nodes {
      ... on PullRequest {
        number
        title
        url
        mergedAt
        repository { name }
      }
    }
  }
}`;

export type MergedPullRequest = {
  number: number;
  title: string;
  url: string;
  mergedAt: string;
  repository: { name: string };
};

/** The signed-in user's pull requests merged in the last MERGED_DAYS. */
export const fetchMergedPullRequests = async (
  scmAuth: ScmAuthApi,
  now = Date.now(),
): Promise<MergedPullRequest[]> => {
  const since = new Date(now - MERGED_DAYS * DAY_MS).toISOString().slice(0, 10);
  const data = await githubGraphql<{ search: { nodes: MergedPullRequest[] } }>(
    scmAuth,
    QUERY,
    { q: `is:pr is:merged author:@me merged:>=${since} org:${GITHUB_ORG}` },
  );
  return data.search.nodes.filter((pr): pr is MergedPullRequest => Boolean(pr));
};

const key = (url: string) => url.toLowerCase().replace(/\/+$/, '');

const clusters = (steps: NonNullable<DeployWorkflowsType['steps']>) =>
  [...new Set(steps.map(step => step.cluster).filter(Boolean))].join(', ');

/**
 * Deploys of the user's merged pull requests that failed, or that have been
 * running for more than STUCK_HOURS. The deployments backend names the pull
 * request a deploy carries (from its commit message) in the workflow `url`,
 * which is matched against the merged pull requests. One row per service and
 * pull request; a failure wins over a stuck run.
 */
export const deployItems = (
  merged: MergedPullRequest[],
  apps: App[],
  now = Date.now(),
): NeedsYouItem[] => {
  const byUrl = new Map(merged.map(pr => [key(pr.url), pr]));
  const items = new Map<string, NeedsYouItem>();
  for (const app of apps) {
    for (const deploy of [...app.runningWorkflows, ...app.recentDeploys]) {
      const pr = deploy.url ? byUrl.get(key(deploy.url)) : undefined;
      if (!pr) continue;
      const steps = deploy.steps ?? [];
      const failed = steps.filter(step => step.phase === 'Failed');
      const running = steps.filter(step => step.phase === 'Running');
      const id = `deploy-${app.name}-${pr.number}`;
      const base = {
        id,
        source: 'deployments' as const,
        // A deploy of your change is broken or stuck: everyone waits on it.
        priority: 'blocking' as const,
        title: pr.title,
        action: {
          label: 'Open',
          href: deploy.kargoUrl ?? '/deployments',
          external: Boolean(deploy.kargoUrl),
        },
      };
      if (failed.length > 0) {
        const since = failed
          .map(step => step.startedAt)
          .filter(Boolean)
          .sort()[0];
        items.set(id, {
          ...base,
          urgency: 'critical',
          label: 'Deploy failed',
          meta: [
            app.name,
            `#${pr.number}`,
            `in ${clusters(failed)}`,
            since && timeAgo(since, now),
          ]
            .filter(Boolean)
            .join(' · '),
          since: since ? Date.parse(since) : undefined,
        });
        continue;
      }
      const stuckSince = running
        .map(step => step.startedAt)
        .filter(
          (startedAt): startedAt is string =>
            !!startedAt && ageMs(startedAt, now) >= STUCK_HOURS * HOUR_MS,
        )
        .sort()[0];
      if (stuckSince && !items.has(id)) {
        items.set(id, {
          ...base,
          urgency: 'warning',
          label: 'Deploy stuck',
          meta: `${app.name} · #${pr.number} · running in ${clusters(running)} for ${Math.floor(
            ageMs(stuckSince, now) / HOUR_MS,
          )} hours`,
          since: Date.parse(stuckSince),
        });
      }
    }
  }
  return [...items.values()];
};
